import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Select, Spin } from "antd";
import { Configuration } from "../../../services/api";
import { parseFormControlValue } from "../../../services/utils";
import { selectAdData } from "../../../routes/AdForm/adForm.ducks";
import styles from "./locationMultiSelect.scss";

// Only Croatia is supported for the wider-area ad flow; backend assigns
// the Hrvatska country id itself, so hard-coding here is fine.
const CROATIA_ID = "1d0ed9f8-c5b3-47f8-8ca1-7feb948d5aef";
const COUNTIES_SCOPE = 3;
const CITIES_SCOPE = 4;

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 2;

const seedLabelsFromAdData = (adData: any) => {
  const seed = {};
  const areas = adData && adData.locationAreas;
  if (Array.isArray(areas)) {
    areas.forEach(a => {
      if (a && a.id) seed[a.id] = a.name;
    });
  }
  return seed;
};

const LocationMultiSelect = (props: any) => {
  const {
    field,
    scopeField,
    value,
    onChange,
    title,
    placeholder = "Odaberite",
    isRequired = false,
  } = props;

  const adData = useSelector(selectAdData);
  const scope = adData ? adData[scopeField] : undefined;

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // id -> name cache. We seed from adData during initialisation so the
  // first render for the edit flow already has names ready, and then
  // extend the cache as the user picks items (via Antd's labelInValue).
  const [labelMap, setLabelMap] = useState(() => seedLabelsFromAdData(adData));

  const selectedIds =
    value && typeof value === "object" && Array.isArray(value[field])
      ? value[field]
      : [];

  const prevScopeRef = useRef(scope);
  const searchTimeoutRef = useRef(null);

  // If adData.locationAreas lands after mount (eg. edit flow where the
  // detail request resolves later), fold the names into the cache.
  useEffect(() => {
    const areas = adData && adData.locationAreas;
    if (!Array.isArray(areas) || areas.length === 0) return;
    setLabelMap(prev => {
      const next = { ...prev };
      areas.forEach(a => {
        if (a && a.id) next[a.id] = a.name;
      });
      return next;
    });
  }, [adData]);

  // Counties fit in a single fetch, so pull them all when the counties
  // scope is picked and let Antd filter client-side.
  useEffect(() => {
    if (scope !== COUNTIES_SCOPE) return;
    let cancelled = false;
    setLoading(true);
    Configuration.locationChildren(CROATIA_ID)
      .then(res => {
        if (cancelled) return;
        const items = (res || []).map(loc => ({ id: loc.id, name: loc.name }));
        setOptions(items);
        setLabelMap(prev => {
          const next = { ...prev };
          items.forEach(it => { next[it.id] = it.name; });
          return next;
        });
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [scope]);

  // When the user flips the radio between counties and cities, drop the
  // picked ids — the old selection isn't valid under the new scope.
  useEffect(() => {
    const prev = prevScopeRef.current;
    prevScopeRef.current = scope;
    const flippedBetweenAreaScopes =
      prev && scope && prev !== scope &&
      (prev === COUNTIES_SCOPE || prev === CITIES_SCOPE) &&
      (scope === COUNTIES_SCOPE || scope === CITIES_SCOPE);
    if (flippedBetweenAreaScopes) {
      if (selectedIds.length > 0) {
        onChange({ [field]: undefined });
      }
      setOptions([]);
    }
  }, [scope]);

  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  // Draft resume: form state is restored from localStorage with ids only
  // (no locationAreas names), so we need to look up each unknown id. For
  // the counties scope the dedicated effect above already pulls the full
  // list; this is mostly for cities, where there is no bulk endpoint.
  const selectedIdsKey = selectedIds.join(",");
  useEffect(() => {
    const unknown = selectedIds.filter(id => id && !labelMap[id]);
    if (unknown.length === 0) return;
    let cancelled = false;
    Promise.all(
      unknown.map(id =>
        Configuration.location(id, () => null)
          .then(loc => (loc && loc.name ? { id, name: loc.name } : null))
      )
    ).then(results => {
      if (cancelled) return;
      setLabelMap(prev => {
        const next = { ...prev };
        results.forEach(entry => {
          if (entry) next[entry.id] = entry.name;
        });
        return next;
      });
    });
    return () => { cancelled = true; };
    // labelMap intentionally omitted — the effect reads the latest value
    // through closure, and including it would re-fire after every pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey]);

  const handleSearch = (query: string) => {
    if (scope !== CITIES_SCOPE) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.length < MIN_SEARCH_CHARS) {
      setOptions([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      Configuration.searchLocations(query, 2)
        .then(res => {
          const items = (res || []).map(loc => ({ id: loc.id, name: loc.name }));
          setOptions(items);
          setLabelMap(prev => {
            const next = { ...prev };
            items.forEach(it => { next[it.id] = it.name; });
            return next;
          });
          setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
  };

  // With labelInValue, each selected tag carries its label along, so we
  // don't need to keep an <Option> for every past pick in the dropdown.
  // Fallback to the id prevents a blank chip if the label hasn't been
  // cached yet (shouldn't happen in practice).
  const selectedLabelInValue = selectedIds.map(id => ({
    key: id,
    label: labelMap[id] || id,
  }));

  const handleChange = (items: any) => {
    const safeItems = Array.isArray(items) ? items : [];
    if (safeItems.length === 0) {
      onChange({ [field]: undefined });
      return;
    }
    // Capture freshly-seen labels (useful when picking from search
    // results — Antd hands us the label we just rendered).
    setLabelMap(prev => {
      const next = { ...prev };
      safeItems.forEach(it => {
        if (typeof it.label === "string") next[it.key] = it.label;
      });
      return next;
    });
    const ids = safeItems.map(it => it.key);
    onChange(parseFormControlValue([field], [ids]));
  };

  const filterOption = scope === COUNTIES_SCOPE
    ? (input, option) => {
      const text = option && option.props && option.props.children;
      return typeof text === "string" &&
        text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    : false;

  return (
    <React.Fragment>
      {title ? (
        <span className={`control-title ${isRequired ? "required" : ""}`}>
          {title}
        </span>
      ) : null}
      <Select
        className={styles.locationMultiSelect}
        mode="multiple"
        labelInValue
        placeholder={placeholder}
        value={selectedLabelInValue}
        onChange={handleChange}
        onSearch={scope === CITIES_SCOPE ? handleSearch : undefined}
        filterOption={filterOption}
        notFoundContent={loading ? <Spin size="small" /> : null}
        showSearch
        style={{ width: "100%" }}
      >
        {options.map(opt => (
          <Select.Option key={opt.id} value={opt.id}>{opt.name}</Select.Option>
        ))}
      </Select>
    </React.Fragment>
  );
};

export default LocationMultiSelect;



// WEBPACK FOOTER //
// ./src/components/FormControls/LocationMultiSelect/LocationMultiSelect.tsx